import { useState, useEffect, useCallback, useRef } from 'react';
import { prepareNewTab, navigateNewTab, openInNewTab } from '@/lib/newTab';
import {
  saveReportOffline,
  isReportOffline,
  getOfflineReports,
  removeOfflineReport,
  openOfflineReport,
  getOfflineStorageUsage,
  formatBytes,
  checkReportFreshness,
  type OfflineReportMeta,
  type OfflineSaveResult,
  type FreshnessResult,
} from '@/services/offlineReportService';

// ─── Network status ──────────────────────────────────────────────────────────

export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { isOnline };
}

// ─── Per-report offline hook ──────────────────────────────────────────────────

export interface UseOfflineReportReturn {
  isOffline: boolean;
  isSaving: boolean;
  isChecking: boolean;
  saveProgress: number;
  saveOffline: (report: Parameters<typeof saveReportOffline>[0]) => Promise<OfflineSaveResult>;
  removeOffline: () => Promise<void>;
  openOffline: () => Promise<string | null>;
  checkFreshness: () => Promise<FreshnessResult>;
}

export function useOfflineReport(reportId: string): UseOfflineReportReturn {
  const [isOffline, setIsOffline] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);

  useEffect(() => {
    let cancelled = false;
    isReportOffline(reportId).then((result) => {
      if (!cancelled) setIsOffline(result);
    });
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  const saveOffline = useCallback(
    async (report: Parameters<typeof saveReportOffline>[0]): Promise<OfflineSaveResult> => {
      setIsSaving(true);
      setSaveProgress(0);
      const result = await saveReportOffline(report, {
        onProgress: (pct) => setSaveProgress(pct),
      });
      setIsSaving(false);
      setSaveProgress(0);
      if (result.success) {
        setIsOffline(true);
      }
      return result;
    },
    []
  );

  const removeOffline = useCallback(async () => {
    await removeOfflineReport(reportId);
    setIsOffline(false);
  }, [reportId]);

  const openOffline = useCallback(async () => {
    return openOfflineReport(reportId);
  }, [reportId]);

  const checkFreshness = useCallback(async (): Promise<FreshnessResult> => {
    setIsChecking(true);
    try {
      return await checkReportFreshness(reportId);
    } finally {
      setIsChecking(false);
    }
  }, [reportId]);

  return { isOffline, isSaving, isChecking, saveProgress, saveOffline, removeOffline, openOffline, checkFreshness };
}

// ─── Offline reports list hook ────────────────────────────────────────────────

export interface UseOfflineReportsReturn {
  offlineReports: OfflineReportMeta[];
  offlineReportIds: Set<string>;
  isLoading: boolean;
  storageUsage: string;
  refresh: () => Promise<void>;
}

export function useOfflineReports(): UseOfflineReportsReturn {
  const [offlineReports, setOfflineReports] = useState<OfflineReportMeta[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [storageUsage, setStorageUsage] = useState('0 B');

  const refresh = useCallback(async () => {
    setIsLoading(true);
    const [reports, bytes] = await Promise.all([
      getOfflineReports(),
      getOfflineStorageUsage(),
    ]);
    setOfflineReports(reports);
    setStorageUsage(formatBytes(bytes));
    setIsLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const offlineReportIds = new Set(offlineReports.map((r) => r.id));

  return { offlineReports, offlineReportIds, isLoading, storageUsage, refresh };
}

// ─── Smart open: online → external URL, offline → Blob URL ───────────────────

export function useSmartOpenFile() {
  const { isOnline } = useNetworkStatus();
  const blobUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    };
  }, []);

  const smartOpen = useCallback(
    async (
      url: string | null | undefined,
      reportId: string,
      options?: { onNoCache?: () => void }
    ) => {
      if (isOnline) {
        if (url) openInNewTab(url);
        return;
      }

      const newWin = prepareNewTab();
      const blobUrl = await openOfflineReport(reportId);
      if (blobUrl) {
        blobUrlsRef.current.push(blobUrl);
        navigateNewTab(newWin, blobUrl);
      } else {
        newWin?.close();
        options?.onNoCache?.();
      }
    },
    [isOnline]
  );

  return { smartOpen, isOnline };
}
